import { cn } from "@/utils/cn";
import { COVERAGE_STATES, formatCount, percent, readPapers } from "../stages";
import type { Coverage } from "../types";

/** One bar for the whole master list: each paper in its coverage state. */
export const CoverageBar = ({ coverage }: { coverage: Coverage }) => {
  const read = readPapers(coverage);
  const shown = COVERAGE_STATES.filter((state) => (coverage.counts[state.id] ?? 0) > 0);
  const summary = shown
    .map((state) => `${state.label}: ${formatCount(coverage.counts[state.id] ?? 0)}`)
    .join(", ");
  return (
    <div className="space-y-2">
      <p className="text-sm text-zinc-700">
        <span className="text-base font-semibold text-zinc-900">{formatCount(read)}</span> of{" "}
        {formatCount(coverage.papers)} papers read ({percent(read, coverage.papers)})
      </p>
      <div
        role="img"
        aria-label={`${formatCount(read)} of ${formatCount(coverage.papers)} papers read. ${summary}.`}
        className="flex h-4 w-full overflow-hidden rounded-[3px] border border-zinc-200 bg-zinc-100"
      >
        {shown.map((state) => (
          <span
            key={state.id}
            className={cn(
              "h-full motion-safe:transition-[width] motion-safe:duration-700",
              state.className,
              state.id === "running" && "motion-safe:animate-pulse",
            )}
            style={{
              width: `${((coverage.counts[state.id] ?? 0) / Math.max(coverage.papers, 1)) * 100}%`,
            }}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-600" aria-label="Papers in each state">
        {shown.map((state) => (
          <li key={state.id} className="flex items-center gap-1.5">
            <span
              className={cn("h-2.5 w-2.5 rounded-sm border border-zinc-200", state.className)}
              aria-hidden
            />
            {state.label}
            <span className="font-medium text-zinc-900">
              {formatCount(coverage.counts[state.id] ?? 0)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
